'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';
import { Turnstile } from 'next-turnstile';

interface TurnstileContextType {
  token: string | null;
  isVerifying: boolean;
  hasToken: () => boolean;
  consumeToken: () => string | null;
}

const TurnstileContext = createContext<TurnstileContextType | undefined>(
  undefined
);

export const useTurnstile = () => {
  const context = useContext(TurnstileContext);
  if (!context) {
    throw new Error('useTurnstile must be used within a TurnstileProvider');
  }
  return context;
};

export const TurnstileProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [token, setToken] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [showWidget, setShowWidget] = useState(false);
  const [widgetKey, setWidgetKey] = useState(0);

  useEffect(() => {
    // Show widget automatically after mount
    setTimeout(() => setShowWidget(true), 100);
  }, []);

  const hasToken = () => {
    return token !== null;
  };

  const consumeToken = () => {
    const currentToken = token;
    setToken(null); // Clear token after use

    // Force widget to reset and ask for new verification
    setWidgetKey((prev) => prev + 1);

    return currentToken;
  };

  const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;

  return (
    <TurnstileContext.Provider
      value={{ token, isVerifying, hasToken, consumeToken }}
    >
      {children}
      {/* Global Turnstile widget - renders automatically */}
      {showWidget && (
        <div
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            zIndex: 9999,
          }}
        >
          <Turnstile
            key={widgetKey}
            siteKey={siteKey || ''}
            retry="auto"
            refreshExpired="auto"
            sandbox={false}
            onVerify={(newToken) => {
              setToken(newToken);
              setIsVerifying(false);
            }}
            onLoad={() => {
              setIsVerifying(true);
              setToken(null);
            }}
            onError={() => {
              setToken(null);
              setIsVerifying(false);
            }}
            onExpire={() => {
              setToken(null);
              setIsVerifying(false);
            }}
            theme="light"
            size="normal"
            appearance="interaction-only"
          />
        </div>
      )}
    </TurnstileContext.Provider>
  );
};
